/**
 * Timeline Tab Component
 * Displays mission status history timeline
 */

import React from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CheckCircle as CompletedIcon,
  EmojiEvents as PendingIcon,
  Autorenew as InProgressIcon,
  Cancel as RejectedIcon,
} from '@mui/icons-material';
import { EmptyState } from '@/components/common';
import type { MissionDetail } from '@/types/mission';
import { MissionStatus } from '@/types/mission';

interface TimelineTabProps {
  mission: MissionDetail;
}

const statusLabels: Record<string, string> = {
  [MissionStatus.PENDING]: 'Pending',
  [MissionStatus.PENDING_ADMIN_REVIEW]: 'Pending Admin Review',
  [MissionStatus.SUBMITTED]: 'Submitted',
  [MissionStatus.GHOST_ASSIGNED]: 'Ghost Assigned',
  [MissionStatus.MISSION_IN_PROGRESS]: 'Mission In Progress',
  [MissionStatus.COMPLETED]: 'Completed',
  [MissionStatus.UNDER_REVIEW]: 'Under Review',
  [MissionStatus.CLOSED]: 'Closed',
  [MissionStatus.CANCELLED]: 'Cancelled',
  [MissionStatus.EXPIRED]: 'Expired',
  'Pending Admin Review': 'Pending Admin Review',
  Approved: 'Approved',
  Rejected: 'Rejected',
  Cancelled: 'Cancelled',
  Closed: 'Closed',
};

const getStatusConfig = (status: string, theme: any) => {
  switch (status) {
    case MissionStatus.COMPLETED:
    case 'Approved':
      return {
        icon: <CompletedIcon sx={{ fontSize: 16 }} />,
        backgroundColor: theme.palette.success.main,
        iconColor: theme.palette.common.white,
      };
    case MissionStatus.PENDING:
    case MissionStatus.PENDING_ADMIN_REVIEW:
    case 'Pending Admin Review':
      return {
        icon: <PendingIcon sx={{ fontSize: 16 }} />,
        backgroundColor: theme.palette.warning.main,
        iconColor: theme.palette.common.white,
      };
    case MissionStatus.MISSION_IN_PROGRESS:
      return {
        icon: <InProgressIcon sx={{ fontSize: 16 }} />,
        backgroundColor: theme.palette.info.main,
        iconColor: theme.palette.common.white,
      };
    case 'Rejected':
    case MissionStatus.CANCELLED:
      return {
        icon: <RejectedIcon sx={{ fontSize: 16 }} />,
        backgroundColor: theme.palette.error.main,
        iconColor: theme.palette.common.white,
      };
    default:
      return {
        icon: <PendingIcon sx={{ fontSize: 16 }} />,
        backgroundColor: theme.palette.grey[600],
        iconColor: theme.palette.common.white,
      };
  }
};

export const TimelineTab: React.FC<TimelineTabProps> = ({ mission }) => {
  const theme = useTheme();

  const sortedStatusHistory = mission.statusHistory
    ? [...mission.statusHistory].sort(
      (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
    )
    : [];

  if (sortedStatusHistory.length === 0) {
    return (
      <EmptyState
        title="No Status History"
        message="No status history available for this mission."
      />
    );
  }

  return (
    <Box>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          marginBottom: theme.spacing(3),
          color: theme.palette.text.primary,
          fontSize: '1.25rem',
        }}
      >
        Status Timeline
      </Typography>

      <Box sx={{ position: 'relative', paddingLeft: theme.spacing(3.5) }}>
        <Box
          sx={{
            position: 'absolute',
            left: theme.spacing(1.75),
            top: 0,
            bottom: 0,
            width: 2,
            backgroundColor: theme.customColors.tableBorder,
          }}
        />

        {sortedStatusHistory.map((item, index) => {
          const config = getStatusConfig(item.status, theme);
          const lastCommaIndex = item.timestampText.lastIndexOf(',');
          let date = item.timestampText;
          let time = '';

          if (lastCommaIndex !== -1) {
            date = item.timestampText.substring(0, lastCommaIndex).trim();
            time = item.timestampText.substring(lastCommaIndex + 1).trim();
          }

          return (
            <Box
              key={index}
              sx={{
                position: 'relative',
                marginBottom: index < sortedStatusHistory.length - 1 ? theme.spacing(3) : 0,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: theme.spacing(-2.75),
                  top: theme.spacing(0.25),
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: config.backgroundColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: config.iconColor,
                  zIndex: 1,
                }}
              >
                {config.icon}
              </Box>

              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                    marginBottom: theme.spacing(0.5),
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                  }}
                >
                  {statusLabels[item.status] || item.status}
                  {item.description && `: ${item.description}`}
                </Typography>
                <Box sx={{ display: 'flex', gap: theme.spacing(0.75), alignItems: 'baseline' }}>
                  {date && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.75rem',
                      }}
                    >
                      {date}
                    </Typography>
                  )}
                  {time && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.75rem',
                      }}
                    >
                      {time}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
